import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { timeAgo } from '../lib/format.js';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M18 15V10a6 6 0 10-12 0v5l-1.5 2.5h15L18 15z" />
    <path d="M10 19a2 2 0 004 0" />
  </svg>
);

const NotificationBell = () => {
  const { counts, setCounts } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapper = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get('/notifications')
      .then((data) => {
        setItems(data.items);
        setCounts((current) => ({ ...current, notifications: data.unread }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, setCounts]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  const normaliseLink = (link) => {
    if (!link) return link;

    if (link === '/landlord/rent-roll') return '/landlord/rent';
    if (link === '/landlord/profile') return '/profile';

    return link;
  };

  const openNotification = async (notification) => {
    setOpen(false);
    if (!notification.read) {
      setItems((current) => current.map((item) => (item.id === notification.id ? { ...item, read: true } : item)));
      setCounts((current) => ({ ...current, notifications: Math.max(0, current.notifications - 1) }));
      api.post(`/notifications/${notification.id}/read`).catch(() => {});
    }
    if (notification.link) navigate(normaliseLink(notification.link));
  };

  const markAll = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    setItems((current) => current.map((item) => ({ ...item, read: true })));
    setCounts((current) => ({ ...current, notifications: 0 }));
  };

  return (
    <div className="bell-wrap" ref={wrapper}>
      <button
        type="button"
        className="icon-btn bell"
        onClick={() => setOpen((value) => !value)}
        aria-label={`Notifications${counts.notifications ? `, ${counts.notifications} unread` : ''}`}
        aria-expanded={open}
      >
        <BellIcon />
        {counts.notifications > 0 ? (
          <span className="bell-count">{counts.notifications > 9 ? '9+' : counts.notifications}</span>
        ) : null}
      </button>

      {open ? (
        <div className="dropdown notification-dropdown" role="menu">
          <header>
            <strong>Notifications</strong>
            {counts.notifications > 0 ? (
              <button type="button" className="link-btn" onClick={markAll}>
                Mark all read
              </button>
            ) : null}
          </header>

          {loading ? <p className="dropdown-note">Loading...</p> : null}
          {!loading && items.length === 0 ? <p className="dropdown-note">Nothing yet.</p> : null}

          <ul>
            {items.map((notification) => (
              <li key={notification.id} className={notification.read ? '' : 'is-unread'}>
                <button type="button" onClick={() => openNotification(notification)}>
                  <strong>{notification.title}</strong>
                  {notification.body ? <span>{notification.body}</span> : null}
                  <em>{timeAgo(notification.createdAt)}</em>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
